<!-- 登录页面 -->
<template>
  <div class="login-container">
    <div class="login-header">
      <img src="@/assets/logo.svg" alt="logo" class="logo" v-if="systemConfig.logo"/>
      <h1>{{ systemConfig.name }}</h1>
    </div>
    <div class="login-box">
      <div class="login-title">用户登录</div>
      <el-form ref="loginForm" :model="loginForm" :rules="loginRules" class="login-form" @submit.native.prevent="handleLogin">
        <el-form-item prop="loginAccount">
          <el-input
            v-model="loginForm.loginAccount"
            placeholder="请输入账号"
            prefix-icon="el-icon-user"
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
        <el-form-item prop="loginPassword">
          <el-input
            v-model="loginForm.loginPassword"
            type="password"
            placeholder="请输入密码"
            prefix-icon="el-icon-lock"
            @keyup.enter.native="handleLogin"
          />
        </el-form-item>
        <el-form-item prop="captcha">
          <div class="captcha-container">
            <el-input
              v-model="loginForm.captcha"
              placeholder="请输入验证码"
              style="width: 200px"
              @keyup.enter.native="handleLogin"
            />
            <img
              :src="captchaImage"
              class="captcha-image"
              @click="refreshCaptcha"
              alt="验证码"
            />
          </div>
        </el-form-item>
        <el-form-item>
          <el-button
            :loading="loading"
            type="primary"
            class="login-button"
            @click.native.prevent="handleLogin"
          >
            登录
          </el-button>
        </el-form-item>
      </el-form>
    </div>
    <div class="login-footer">
      {{ systemConfig.copyright }}
    </div>
  </div>
</template>

<script>
import { login, getCaptcha } from '@/api/auth'
import { getSystemConfig } from '@/api/system'

export default {
  name: 'Login',
  data() {
    return {
      loginForm: {
        loginAccount: '',
        loginPassword: '',
        captcha: ''
      },
      loginRules: {
        loginAccount: [
          { required: true, message: '请输入账号', trigger: 'blur' }
        ],
        loginPassword: [
          { required: true, message: '请输入密码', trigger: 'blur' }
        ],
        captcha: [
          { required: true, message: '请输入验证码', trigger: 'blur' }
        ]
      },
      loading: false,
      captchaImage: '',
      systemConfig: {
        name: '',
        shortName: '',
        logo: '',
        copyright: ''
      }
    }
  },
  created() {
    this.refreshCaptcha()
    this.loadSystemConfig()
    // 设置页面标题
    document.title = this.systemConfig.name || '系统登录'
  },
  methods: {
    // 加载系统配置
    async loadSystemConfig() {
      try {
        const res = await getSystemConfig()
        this.systemConfig = res.data
        document.title = this.systemConfig.name || '系统登录'
      } catch (error) {
        console.error('获取系统配置失败:', error)
      }
    },
    // 刷新验证码
    async refreshCaptcha() {
      try {
        const res = await getCaptcha()
        this.captchaImage = res.data.image
      } catch (error) {
        console.error('获取验证码失败:', error)
        this.$message.error('获取验证码失败')
      }
    },
    // 处理登录
    handleLogin() {
      this.$refs.loginForm.validate(async valid => {
        if (!valid) return
        
        this.loading = true
        try {
          const res = await login(this.loginForm)
          // 保存token
          localStorage.setItem('token', res.data.token)
          // 持久化存储用户信息对象
          const userInfo = {
            userId: res.data.userId,
            userName: res.data.userName,
            loginAccount: res.data.loginAccount,
            roleType: res.data.roleType,
            tenantId: res.data.tenantId
          }
          localStorage.setItem('userInfo', JSON.stringify(userInfo))
          // 兼容原有单字段存储（可选，便于老代码使用）
          localStorage.setItem('userRole', res.data.roleType)
          localStorage.setItem('userId', res.data.userId)
          localStorage.setItem('userName', res.data.userName)
          // 获取重定向地址
          const redirect = this.$route.query.redirect || '/'
          // 跳转到目标页面
          this.$router.push(redirect)
          this.$message.success('登录成功')
        } catch (error) {
          console.error('登录失败:', error)
          this.refreshCaptcha()
          this.loginForm.captcha = ''
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.login-container {
  min-height: 100vh;
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #1890ff 0%, #1d39c4 100%);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('../assets/login-bg.svg') center/cover no-repeat;
    opacity: 0.1;
  }
}

.login-header {
  text-align: center;
  margin-bottom: 40px;
  z-index: 1;

  .logo {
    width: 84px;
    height: 84px;
    margin-bottom: 16px;
  }

  h1 {
    color: #fff;
    font-size: 28px;
    font-weight: 500;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }
}

.login-box {
  width: 400px;
  padding: 40px;
  background: rgba(255, 255, 255, 0.98);
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 1;
  
  .login-title {
    font-size: 24px;
    color: #333;
    text-align: center;
    margin-bottom: 30px;
    font-weight: 500;
  }
}

.login-form {
  ::v-deep .el-input__inner {
    height: 40px;
    line-height: 40px;
    border-radius: 4px;
    border: 1px solid #dcdfe6;
    
    &:focus {
      border-color: #1890ff;
    }
  }
}

.captcha-container {
  display: flex;
  align-items: center;
  gap: 12px;

  .captcha-image {
    height: 40px;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.3s;

    &:hover {
      opacity: 0.8;
    }
  }
}

.login-button {
  width: 100%;
  height: 40px;
  border-radius: 4px;
  font-size: 16px;
  letter-spacing: 2px;
  background: #1890ff;
  border-color: #1890ff;
  
  &:hover, &:focus {
    background: #40a9ff;
    border-color: #40a9ff;
  }
}

.login-footer {
  position: absolute;
  bottom: 20px;
  color: rgba(255, 255, 255, 0.8);
  font-size: 14px;
  text-align: center;
  z-index: 1;
}

/* 响应式设计 */
@media (max-width: 480px) {
  .login-box {
    width: 90%;
    padding: 30px 20px;
  }
}
</style> 